import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TINYCMS_SEARCH,
  REMOVE_TINYCMS_SEARCH_OWNER,
  REQUEST_TINYCMS_SEARCH_OWNER,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { SETTINGS_HISTORY_QUERY } from '../settings/SettingsHistory';
import { Loader } from '../common/PageLoader';
import { showInkToast } from './InkToast';

/**
 * Manage › Site › Search engines (below the Allow/Block choice): where Google stands with each of the site's
 * addresses, proven by tinycms itself (apiserver searchConsole.js), and
 * "Add me as an owner", which puts the person's Google account on the same
 * property so they open Search Console themselves, and Remove, which the
 * engine takes off the property on its next run (the last owner stays).
 * Nothing here waits on Google: it shows what the engine last reported.
 */

type Owner = {
  email: string;
  requestedAt?: string;
  grantedAt: string | null;
  error?: string | null;
  /** Remove was asked for; the engine hasn't taken them off Google yet. */
  removeRequestedAt?: string | null;
};

const KEEP_ONE_OWNER = 'Keep at least one owner. Add another first.';
type Host = {
  host: string;
  status: 'pending' | 'verified' | 'failed' | 'redirected';
  verifiedAt: string | null;
  sitemapSubmittedAt: string | null;
  sitemapLastReadAt: string | null;
  sitemapPending: boolean;
  sitemapErrors: number | null;
  sitemapWarnings: number | null;
  sitemapListed: number | null;
  grantedOwners: Owner[];
  lastError: string | null;
  consoleUrl: string;
};

const ROOT_FILES = '/content?view=manage&area=code#verification';

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The sitemap, for the card's status line: "sitemap read 26 Oct, 67 pages". */
function sitemapPhrase(h: Host): string {
  if (!h.sitemapSubmittedAt) return 'sitemap submitting next';
  if (h.sitemapLastReadAt) {
    const parts = [`sitemap read ${when(h.sitemapLastReadAt)}`];
    if (h.sitemapListed != null) parts.push(plural(h.sitemapListed, 'page', 'pages'));
    if (h.sitemapErrors) parts.push(plural(h.sitemapErrors, 'error', 'errors'));
    else if (h.sitemapWarnings) parts.push(plural(h.sitemapWarnings, 'warning', 'warnings'));
    return parts.join(', ');
  }
  return h.sitemapPending
    ? 'sitemap submitted, Google hasn’t read it yet'
    : `sitemap submitted ${when(h.sitemapSubmittedAt)}`;
}

function OwnerForm({
  host,
  canManage,
  pending,
}: {
  host: Host;
  canManage: boolean;
  pending?: boolean;
}) {
  const { accountId } = useAccount();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [request, { loading }] = useMutation(REQUEST_TINYCMS_SEARCH_OWNER, {
    refetchQueries: [GET_TINYCMS_SEARCH, SETTINGS_HISTORY_QUERY],
  });
  const [removeOwner] = useMutation(REMOVE_TINYCMS_SEARCH_OWNER, {
    refetchQueries: [GET_TINYCMS_SEARCH, SETTINGS_HISTORY_QUERY],
    awaitRefetchQueries: true,
  });
  const [removing, setRemoving] = useState<string | null>(null);
  const owners = host.grantedOwners || [];
  const staying = owners.filter((o) => !o.removeRequestedAt).length;

  const remove = async (o: Owner) => {
    if (removing) return;
    // The server says the same; saying it here saves the round trip.
    if (staying <= 1) {
      showInkToast(KEEP_ONE_OWNER);
      return;
    }
    setRemoving(o.email);
    try {
      const { data } = await removeOwner({
        variables: { accountId, host: host.host, email: o.email },
      });
      showInkToast(
        `${data?.removeTinycmsSearchOwner?.email || o.email} removed from Search Console.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setRemoving(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await request({ variables: { accountId, host: host.host, email } });
      const out = data?.requestTinycmsSearchOwner;
      if (out?.added) {
        showInkToast(
          pending
            ? `Once Google knows ${host.host}, we add ${out.email} as an owner.`
            : `We’re adding ${out.email} as an owner of ${host.host} in Google.`
        );
        setEmail('');
      } else if (out) {
        showInkToast(`${out.email} is already on the list.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  if (!owners.length && !canManage) return null;
  return (
    <div className="sc-card-body">
      <div className="sc-label">Owners</div>
      {owners.length ? (
        <ul className="sc-owners" aria-label="Owners in Google Search Console">
          {owners.map((o) => (
            <li key={o.email}>
              <span className="sc-email">{o.email}</span>
              <span className="sc-when">
                {o.removeRequestedAt
                  ? o.error
                    ? `Google didn’t remove it yet: ${o.error}. We try again tomorrow.`
                    : 'Being removed…'
                  : o.grantedAt
                    ? `Owner since ${when(o.grantedAt)}`
                    : o.error
                      ? `Google didn’t accept it yet: ${o.error}. We try again tomorrow.`
                      : pending
                        ? 'Added once Google knows the site'
                        : 'Being added…'}
              </span>
              {canManage ? (
                o.removeRequestedAt || removing === o.email ? (
                  <span className="sc-remove" aria-live="polite">
                    Removing…
                  </span>
                ) : (
                  <button
                    type="button"
                    className="sc-remove"
                    onClick={() => void remove(o)}
                    disabled={Boolean(removing)}
                    aria-label={`Remove ${o.email} from Search Console`}
                  >
                    Remove
                  </button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {canManage ? (
        <form className="sc-owner-form" onSubmit={(e) => void submit(e)}>
          <input
            className="ink-input"
            type="email"
            placeholder="you@gmail.com"
            aria-label="Google account email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            className="ink-btn ink-btn-primary"
            disabled={loading || !email.trim()}
          >
            {loading ? 'Adding…' : 'Add owner'}
          </button>
          {error ? (
            <span className="dm-p" role="alert">
              {error}
            </span>
          ) : null}
        </form>
      ) : null}
      {/* Seen on the first real run (2026-10-26): Google shows a delegated owner
          an access page once; "Verify ownership" confirms at once. */}
      <div className="sc-hint">
        {pending
          ? 'Owners can open Search Console for this site. We add them as soon as Google knows it.'
          : 'Owners can open Search Console for this site. On your first visit Google may say you don’t have access: click Verify ownership and it confirms at once.'}
      </div>
    </div>
  );
}

/** One Google Search Console card per address the site answers on. */
function HostRow({
  h,
  primary,
  canManage,
  many,
}: {
  h: Host;
  primary: string;
  canManage: boolean;
  many: boolean;
}) {
  const isPrimary = h.host === primary;
  if (h.status === 'redirected') {
    return (
      <div className="sc-redirect">
        {h.host} sends visitors to {primary}; Google follows.
      </div>
    );
  }
  const verified = h.status === 'verified';
  return (
    <div className="sc-card">
      <div className="sc-card-head">
        <div className="sc-card-title">
          <div className="sc-name">
            Google Search Console
            {many ? <span className="sc-host-name"> · {h.host}</span> : null}
          </div>
          <div className="sc-status">
            <span
              className="sc-dot"
              data-tone={verified ? 'ok' : h.status === 'failed' ? 'warn' : 'wait'}
            />
            <span>
              {verified
                ? `Verified ${when(h.verifiedAt)} · ${sitemapPhrase(h)}`
                : h.status === 'failed'
                  ? `Google hasn’t accepted ${h.host} yet. We try again every day; nothing for you to do.`
                  : `Telling Google about ${h.host}. Usually within the hour.`}
              {!verified && h.lastError ? ` Last time: ${h.lastError}` : ''}
            </span>
          </div>
        </div>
        {verified ? (
          <a className="ink-btn" href={h.consoleUrl} target="_blank" rel="noreferrer">
            Open Search Console ↗
          </a>
        ) : null}
      </div>
      {verified && !isPrimary ? (
        <div className="sc-card-note">
          Google moves to {primary} as it follows the redirects, over a few weeks. In Search
          Console, “Change of address” speeds that up.
        </div>
      ) : null}
      {/* Asking before it verifies is fine: the engine adds owners right after. */}
      <OwnerForm host={h} canManage={canManage} pending={!verified} />
    </div>
  );
}

/**
 * `blocked` is the Allow/Block switch above (the account, so it is right the
 * moment it changes); while blocked this says only that, with who chose it
 * and when once the server has said (info.blocked, from the audit).
 */
export function SearchConsole({ blocked = false }: { blocked?: boolean }) {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_SEARCH, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const info = data?.tinycmsSearch;
  if (blocked) {
    const by: { at?: string; by?: string | null } | undefined = info?.blocked;
    return (
      <p className="ink-form-hint">
        Search engines are blocked for this site, so nothing is submitted
        {by?.at ? ` (${by.by ? `blocked by ${by.by}` : 'blocked'} on ${when(by.at)})` : ''}. Choose
        Allow above to be listed again.
      </p>
    );
  }
  if (loading && !info) return <Loader />;
  if (!info) return null;
  const hosts: Host[] = info.hosts || [];
  const primary: string = info.origin ? new URL(info.origin).hostname : '';
  const canManage = Boolean(info.canManage);

  return (
    <div className="dm-stack">
      {!info.available ? (
        <div className="dm-p">Search isn’t switched on for this site yet.</div>
      ) : !info.live ? (
        <div className="dm-p">Once your site is public, tinycms tells Google about it here.</div>
      ) : (
        hosts.map((h) => (
          <HostRow
            key={h.host}
            h={h}
            primary={primary}
            canManage={canManage}
            many={hosts.filter((x) => x.status !== 'redirected').length > 1}
          />
        ))
      )}
      <div className="sc-foot">
        Set up Search Console yourself? Pick a URL-prefix property and paste Google’s tag in{' '}
        <Link to={ROOT_FILES}>Verification &amp; ads.txt</Link>.
      </div>
    </div>
  );
}
